import { useEffect, useState } from "react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Badge,
  Box,
  VStack,
  Text,
  HStack,
  Link,
  Button,
  Spinner,
} from "@chakra-ui/react"
import { RefreshCw } from "lucide-react"
import useNotificationChannelStore from "@/store/NotificationChannelStore"
import useNotificationSubscriptionStore from "@/store/NotificationSubscriptionStore"

interface SelectNotificationChannelDialogProps {
  isOpen: boolean
  onClose: () => void
  onSave: (selectedChannelIds: string[]) => void
  featureCode: string
  subscriptionId?: string
}

const getChannelTypeColor = (type: string) => {
  switch (type.toLowerCase()) {
    case "slack":
      return "purple"
    case "discord":
      return "blue"
    case "line":
      return "green"
    case "email":
      return "orange"
    default:
      return "gray"
  }
}

export default function SelectNotificationChannelDialog({
  isOpen,
  onClose,
  onSave,
  featureCode,
  subscriptionId,
}: SelectNotificationChannelDialogProps) {
  const [selectedChannels, setSelectedChannels] = useState<string[]>([])
  const [isRefreshing, setIsRefreshing] = useState(false)
  const { channels, fetchChannels } = useNotificationChannelStore()
  const { findSubscription } = useNotificationSubscriptionStore()

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      await fetchChannels()
    } catch (error) {
      console.error("Error refreshing channels:", error)
    } finally {
      setIsRefreshing(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      fetchChannels()
    }
  }, [isOpen, fetchChannels])

  useEffect(() => {
    const loadExistingChannels = async () => {
      if (subscriptionId && isOpen) {
        try {
          const result = await findSubscription(featureCode, subscriptionId)
          if (result.success && result.data) {
            setSelectedChannels(
              result.data.channels.map((channel) => channel.id),
            )
          }
        } catch (error) {
          console.error("Error loading existing channels:", error)
        }
      }
    }
    loadExistingChannels()
  }, [isOpen, featureCode, subscriptionId, findSubscription])

  const handleSave = () => {
    onSave(selectedChannels)
    onClose()
  }

  const handleCheckboxChange = (channelId: string, checked: boolean) => {
    setSelectedChannels((prev) =>
      checked ? [...prev, channelId] : prev.filter((id) => id !== channelId),
    )
  }

  return (
    <AlertDialog open={isOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>選擇通知方式</AlertDialogTitle>
          <AlertDialogDescription>
            請選擇要接收通知的方式，您可以在{" "}
            <Link href="/notification-channels" isExternal>
              這邊
            </Link>{" "}
            新增通知方式
          </AlertDialogDescription>
        </AlertDialogHeader>

        <VStack
          align="stretch"
          spacing={2}
          maxHeight="400px"
          overflowY="auto"
          py={4}
          position="relative">
          {isRefreshing ? (
            <Box display="flex" justifyContent="center" py={8}>
              <Spinner size="lg" />
            </Box>
          ) : (
            <>
              {channels.map((channel) => (
                <Box
                  key={channel.id}
                  p={3}
                  border="1px"
                  borderColor="gray.200"
                  rounded="md"
                  _hover={{ bg: "gray.50" }}>
                  <HStack justify="space-between">
                    <Box
                      as="button"
                      onClick={() =>
                        handleCheckboxChange(
                          channel.id,
                          !selectedChannels.includes(channel.id),
                        )
                      }
                      cursor="pointer"
                      width="100%">
                      <HStack>
                        <Checkbox
                          id={channel.id}
                          checked={selectedChannels.includes(channel.id)}
                          onCheckedChange={(checked) =>
                            handleCheckboxChange(channel.id, checked as boolean)
                          }
                        />
                        <Badge colorScheme={getChannelTypeColor(channel.type)}>
                          {channel.type.toUpperCase()}
                        </Badge>
                        <Text>{channel.name}</Text>
                      </HStack>
                    </Box>
                  </HStack>
                </Box>
              ))}
              {channels.length === 0 && (
                <Text color="gray.500" textAlign="center" py={2}>
                  尚未設定任何通知方式
                </Text>
              )}
            </>
          )}
        </VStack>

        <AlertDialogFooter>
          <HStack spacing={4} width="full" justify="space-between">
            <Button
              aria-label="Refresh channels"
              onClick={handleRefresh}
              isLoading={isRefreshing}
              variant="ghost"
              size="sm"
              leftIcon={<RefreshCw size={16} />}>
              重新整理
            </Button>
            <HStack>
              <AlertDialogCancel onClick={onClose}>取消</AlertDialogCancel>
              <AlertDialogAction onClick={handleSave}>儲存</AlertDialogAction>
            </HStack>
          </HStack>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
